"use client";

import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

interface TrendingItem {
	model: string;
	provider: string;
	currentWeek: number;
	previousWeek: number;
	growthPercent: number;
}

interface TrendingChartProps {
	data: TrendingItem[];
	isLoading?: boolean;
}

const CustomTooltip = ({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ name: string; value: number }>;
}) => {
	if (active && payload?.[0]) {
		const item = payload[0];
		return (
			<div className="rounded-lg border bg-popover text-popover-foreground p-3 shadow-sm">
				<p className="text-sm text-muted-foreground">{item.name}</p>
				<p className="font-semibold">{item.value}%</p>
			</div>
		);
	}
	return null;
};

export function TrendingChart({ data, isLoading }: TrendingChartProps) {
	if (isLoading) {
		return (
			<div className="flex h-[350px] items-center justify-center">
				<div className="text-sm text-muted-foreground">Loading chart...</div>
			</div>
		);
	}

	if (!data || data.length === 0) {
		return (
			<div className="flex h-[350px] items-center justify-center">
				<div className="text-sm text-muted-foreground">No data available</div>
			</div>
		);
	}

	// Sort by growth and take top 10
	const chartData = data
		.sort((a, b) => b.growthPercent - a.growthPercent)
		.slice(0, 10)
		.map((item) => ({
			name: `${item.model}`,
			growth: item.growthPercent,
			label: `${item.model} (${item.provider})`,
		}));

	return (
		<ResponsiveContainer width="100%" height={350}>
			<BarChart
				data={chartData}
				margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
			>
				<CartesianGrid strokeDasharray="3 3" />
				<XAxis
					dataKey="name"
					angle={-45}
					textAnchor="end"
					height={100}
					interval={0}
					tick={{ fontSize: 12 }}
				/>
				<YAxis
					label={{ value: "Growth %", angle: -90, position: "insideLeft" }}
				/>
				<Tooltip content={<CustomTooltip />} />
				<Bar
					dataKey="growth"
					fill="#3b82f6"
					name="Week-over-Week Growth"
					radius={[8, 8, 0, 0]}
				/>
			</BarChart>
		</ResponsiveContainer>
	);
}
